import React, { useEffect, useState } from 'react';
import {
  Badge, DataTable, IdCell, PageHead, SearchBox, StatCard, Tabs, dateTimeStr, inr, num, titleCase,
} from '../components/ui';
import useList from '../hooks/useList';
import { inventoryApi } from '../api';

const MOVEMENT_TONES = { in: 'success', out: 'danger', reserve: 'warn', release: 'info', adjust: 'muted' };

const Inventory = () => {
  const [tab, setTab] = useState('stock');
  const [summary, setSummary] = useState(null);

  const stock = useList(inventoryApi.list, { search: '', lowStock: '' }, { auto: true });
  const moves = useList(inventoryApi.movements, { type: '' }, { auto: false });

  useEffect(() => {
    inventoryApi.summary().then(res => setSummary(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'movements') moves.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const stockColumns = [
    {
      key: 'product',
      label: 'Product',
      render: row => <IdCell image={row.product?.thumbnail} name={row.product?.name} sub={row.product?.sku} />,
    },
    {
      key: 'warehouse',
      label: 'Warehouse',
      render: row => (
        <>
          <div>{row.warehouse?.name || '—'}</div>
          <div className="cell-muted">{row.warehouse?.code}</div>
        </>
      ),
    },
    { key: 'quantity', label: 'On hand', align: 'right', render: row => <strong>{num(row.quantity)}</strong> },
    { key: 'reservedQuantity', label: 'Reserved', align: 'right', render: row => num(row.reservedQuantity) },
    {
      key: 'available',
      label: 'Available',
      align: 'right',
      render: row => (
        <strong style={{ color: row.availableQuantity > 0 ? 'var(--success)' : 'var(--danger)' }}>
          {num(row.availableQuantity)}
        </strong>
      ),
    },
    { key: 'minLevel', label: 'Min level', align: 'right', render: row => num(row.minLevel) },
    {
      key: 'health',
      label: 'Health',
      render: row =>
        row.quantity === 0 ? <Badge status="failed">Out of stock</Badge>
          : row.quantity <= row.minLevel ? <Badge status="pending">Low</Badge>
            : <Badge status="active">Healthy</Badge>,
    },
  ];

  const moveColumns = [
    { key: 'createdAt', label: 'When', render: row => <span className="text-sm">{dateTimeStr(row.createdAt)}</span> },
    { key: 'type', label: 'Type', render: row => <Badge status={row.type} tone={MOVEMENT_TONES[row.type]}>{titleCase(row.type)}</Badge> },
    { key: 'product', label: 'Product', render: row => <IdCell image={row.product?.thumbnail} name={row.product?.name} sub={row.product?.sku} size="sm" /> },
    { key: 'warehouse', label: 'Warehouse', render: row => row.warehouse?.name || '—' },
    {
      key: 'quantity',
      label: 'Qty',
      align: 'right',
      render: row => (
        <strong style={{ color: row.quantity >= 0 ? 'var(--success)' : 'var(--danger)' }}>
          {row.quantity > 0 ? '+' : ''}{num(row.quantity)}
        </strong>
      ),
    },
    { key: 'balance', label: 'Balance', align: 'right', render: row => `${num(row.quantityBefore)} → ${num(row.quantityAfter)}` },
    { key: 'reference', label: 'Reference', render: row => <span className="text-sm text-muted">{row.reference || row.reason || '—'}</span> },
    { key: 'performedBy', label: 'By', render: row => <span className="text-sm">{row.performedBy?.name || 'System'}</span> },
  ];

  return (
    <>
      <PageHead title="Inventory" subtitle="Live stock across every warehouse, with the full movement ledger." />

      {summary && (
        <div className="stat-grid stagger mb-2">
          <StatCard accent label="Stock value" value={inr(summary.stockValue, true)} icon="⛁" foot={`${num(summary.totalSkus)} SKU rows`} />
          <StatCard label="Units on hand" value={num(summary.totalQuantity)} icon="▦" />
          <StatCard label="Reserved" value={num(summary.reservedQuantity)} icon="🔒" foot="Committed to accepted orders" />
          <StatCard label="Available to sell" value={num(summary.availableQuantity)} icon="✓" />
          <StatCard label="Low stock" value={num(summary.lowStockCount)} icon="⚠" onClick={() => { setTab('stock'); stock.setFilter('lowStock', 'true'); }} />
          <StatCard label="Out of stock" value={num(summary.outOfStockCount)} icon="⛔" />
        </div>
      )}

      <Tabs
        tabs={[{ value: 'stock', label: 'Stock levels' }, { value: 'movements', label: 'Movement ledger' }]}
        value={tab}
        onChange={setTab}
      />

      {tab === 'stock' ? (
        <>
          <div className="filters">
            <SearchBox value={stock.filters.search} onChange={v => stock.setFilter('search', v)} placeholder="Search product or SKU…" />
            <select className="select" style={{ width: 180 }} value={stock.filters.lowStock} onChange={e => stock.setFilter('lowStock', e.target.value)}>
              <option value="">All stock rows</option>
              <option value="true">Low stock only</option>
            </select>
            <div className="grow" />
            <span className="text-muted text-sm">{stock.meta ? `${num(stock.meta.total)} rows` : ''}</span>
          </div>
          <DataTable columns={stockColumns} rows={stock.rows} loading={stock.loading} meta={stock.meta} onPage={stock.setPage} />
        </>
      ) : (
        <>
          <div className="filters">
            <select className="select" style={{ width: 200 }} value={moves.filters.type} onChange={e => moves.setFilter('type', e.target.value)}>
              <option value="">All movement types</option>
              <option value="in">Stock in</option>
              <option value="out">Stock out</option>
              <option value="reserve">Reserved</option>
              <option value="release">Released</option>
              <option value="adjust">Adjustments</option>
            </select>
            <div className="grow" />
            <span className="text-muted text-sm">{moves.meta ? `${num(moves.meta.total)} movements` : ''}</span>
          </div>
          <DataTable columns={moveColumns} rows={moves.rows} loading={moves.loading} meta={moves.meta} onPage={moves.setPage} />
        </>
      )}
    </>
  );
};

export default Inventory;
